import React from 'react';
import { Stack, Typography, SxProps, IconButton } from '@mui/material';
import Icon from '@mdi/react';
import { mdiArrowExpandHorizontal, mdiFormatHorizontalAlignCenter, mdiClose } from '@mdi/js';
import { headingFontBold } from '../../../Theme';
import DocTypeIcon from '../Icons/DocTypeIcon';
import AppTooltip from '../AppTooltip/AppTooltip';

/** Stack styles for the wrapper. */
const stackSx: SxProps = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    height: 60,
};

/** Ellipsis text styles. */
const ellipsisSx: SxProps = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '220px',
    flexGrow: 1,
};

/** Wrapper props. */
const wrapperSx: SxProps = {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
};

/** Expand/Collapse IconButton styles. */
const iconButtonSx: SxProps = {
    '&.MuiIconButton-root': {
        '&:hover': {
            borderRadius: '25%',
        },
    },
};

export default (props: ISidepanelHeaderProps) => {
    const { type, firtsRowTitle, secondRowTitle, handleClose, handleExpand, isExpanded, sx } =
        props;
    return (
        <Stack sx={{ ...stackSx, ...sx }}>
            <Stack
                sx={{
                    flex: 1,
                    pl: 1,
                }}
            >
                <Stack sx={wrapperSx}>
                    <DocTypeIcon type={type} color='currentColor' />
                    <AppTooltip title={firtsRowTitle} placement='top-start'>
                        <Typography
                            variant='h6'
                            sx={{
                                ...headingFontBold,
                                ...ellipsisSx,
                                fontSize: 16,
                            }}
                        >
                            {firtsRowTitle}
                        </Typography>
                    </AppTooltip>
                </Stack>
                <Stack sx={wrapperSx}>
                    <AppTooltip title={secondRowTitle} placement='top-start'>
                        <Typography sx={{ ...ellipsisSx, fontSize: 14, pl: 0.5 }}>
                            {secondRowTitle}
                        </Typography>
                    </AppTooltip>
                </Stack>
            </Stack>
            <Stack
                sx={{
                    flexGrow: 0,
                    flexDirection: 'row',
                    alignSelf: 'flex-start',
                }}
            >
                <IconButton sx={iconButtonSx} onClick={handleExpand}>
                    <Icon
                        path={
                            isExpanded ? mdiFormatHorizontalAlignCenter : mdiArrowExpandHorizontal
                        }
                        size={1}
                    />
                </IconButton>
                <IconButton onClick={handleClose}>
                    <Icon path={mdiClose} size={1} />
                </IconButton>
            </Stack>
        </Stack>
    );
};

/**
 * Interface that describes the props for the Sidepanel's Header.
 */
export interface ISidepanelHeaderProps {
    /** Type of the DocTypeIcon. */
    type: string;

    /** Text for the first row. */
    firtsRowTitle: string;

    /** Text for the secobd row. */
    secondRowTitle: string;

    /** Close Button onClick handler. */
    handleClose: () => void;

    /** Expand Button onClick handler. */
    handleExpand: () => void;

    /** Flag that shows if the sidepanel is expanded. */
    isExpanded: boolean;

    /** Extra styles for the Stack wrapper of the component. */
    sx?: SxProps;
}
